import React, { useMemo } from 'react';
import { CircularProgress, ListSubheader, MenuItem, TextField } from '@material-ui/core';
import clsx from 'clsx';
import useStyles from 'app/cluster-styles/useStyles';
import useProviderData from '../hooks/useProviderData';

/**
 * Geography headings in the order people look for them, rather than
 * alphabetically: North America first, then Europe, then everything else by
 * name. Neither cloud groups its regions this way itself - AWS has no grouping
 * field at all and Azure lists US, Canada and Mexico separately - so the order
 * lives here, shared by both.
 */
const GROUP_ORDER = ['US', 'US (GovCloud)', 'Canada', 'Mexico', 'Europe', 'UK'];

// Matches the group headings in InstanceSizeSelector, so the two dropdowns in
// the same card look like siblings.
//
// top: 0, not the -8 the Autocomplete uses: that value cancels the Autocomplete
// listbox's own padding, and a Select's MenuList pads itself instead - copying
// it left a white strip above the first heading and pulled the heading up over
// the row below it. The MenuList padding is removed below so the heading sits
// flush and rows scroll cleanly underneath.
const groupHeadingStyle = {
    position: 'sticky',
    top: 0,
    zIndex: 1,
    backgroundColor: '#f8fafc',
    color: '#475569',
    fontSize: 11,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    padding: '6px 16px',
    lineHeight: 1.6,
    borderBottom: '1px solid #e2e8f0',
};

/**
 * Region dropdown.
 *
 * By default the list comes from the provider catalog (providers.json). A
 * caller that knows the regions live — the AWS form asks the account — passes
 * `options` and the catalog is not consulted at all. `loading` shows a spinner
 * row; `disabled` + `disabledLabel` cover "pick a credential first".
 */
export default function RegionSelector({
    provider, value, onChange, allowedRegionIds, className,
    options, loading = false, disabled = false, disabledLabel, error = false, helperText,
}) {
    const classes = useStyles();
    const { regions: catalogRegions } = useProviderData(provider);
    const regions = options || catalogRegions;

    const visibleRegions = useMemo(() => {
        if (!allowedRegionIds || allowedRegionIds.length === 0) return regions;
        return regions.filter((r) => allowedRegionIds.includes(r.region_id));
    }, [regions, allowedRegionIds]);

    const safeValue = regions.some((r) => r.region_id === value) ? value : '';
    const isDisabled = disabled || loading;

    // [[group, regions], ...] in group order, or null when nothing is grouped.
    const grouped = useMemo(() => {
        if (!visibleRegions.some((r) => r.group)) return null;
        const byGroup = {};
        visibleRegions.forEach((r) => {
            const group = r.group || 'Other';
            (byGroup[group] = byGroup[group] || []).push(r);
        });
        const rank = (group) => {
            const i = GROUP_ORDER.indexOf(group);
            // Anything unlisted sorts after the named geographies, alphabetically.
            return i === -1 ? GROUP_ORDER.length : i;
        };
        return Object.keys(byGroup)
            .sort((a, b) => (rank(a) - rank(b)) || a.localeCompare(b))
            .map((group) => [group, byGroup[group]]);
    }, [visibleRegions]);

    const rowFor = (region) => (
        <MenuItem key={region.region_id} value={region.region_id}>
            {region.name}
        </MenuItem>
    );

    return (
        <TextField
            select
            className={clsx(classes.input, className || "flex-1")}
            id="rm-region"
            name="rm_region_id"
            label={disabled && disabledLabel ? disabledLabel : 'Region'}
            variant="outlined"
            value={safeValue}
            onChange={(e) => { if (e.target.value) onChange(e.target.value); }}
            required={!disabled}
            disabled={isDisabled}
            error={error}
            helperText={helperText}
            SelectProps={{
                MenuProps: {
                    PaperProps: {
                        style: { maxHeight: 300 },
                    },
                    // No list padding: it is what a sticky heading would otherwise
                    // float over, leaving a white strip at the top of the menu.
                    MenuListProps: grouped ? { style: { paddingTop: 0, paddingBottom: 0 } } : undefined,
                },
            }}
        >
            {loading && (
                <MenuItem disabled>
                    <CircularProgress size={16} className="mr-8" />
                    Loading regions...
                </MenuItem>
            )}
            {grouped
                ? grouped.reduce((rows, [group, inGroup]) => rows.concat(
                    <ListSubheader key={`group-${group}`} disableSticky style={groupHeadingStyle}>
                        {group}
                    </ListSubheader>,
                    inGroup.map(rowFor)
                ), [])
                : visibleRegions.map(rowFor)}
        </TextField>
    );
}
